// SPDX-License-Identifier: BUSL-1.1

/**
 * Per-job live activity log (NocoDB #4790 Phase 1a).
 *
 * Rendered inside <JobDetailSheet>. Subscribes to the org-wide fleet
 * stream context provided by <FleetActivityStreamProvider> on
 * agent-detail.tsx, then filters events by:
 *
 *   payload.task_id === job.id
 *
 * As of 2026-05-19 only `task_started` and `task_completed` payloads
 * carry `task_id` per the Zod schemas in `activity-payload-schemas.ts`.
 * The pi-SDK runner currently only sets task_id on those two kinds.
 * NocoDB #4791 (Phase 1b) extends the runner to set task_id on
 * tool_call/artifact/decision/blocker/context_compressed events
 * emitted within a job's processing window — once that ships, this
 * same component renders the full per-tool log without any UI change.
 * The filter is the SDK-agnostic contract.
 *
 * Empty state covers three cases:
 *   1. Modal opened on a long-completed job → nothing in the buffer
 *      (fleet stream only delivers events from subscribe-time forward)
 *   2. Modal opened while job is processing but agent quiet → wait msg
 *   3. Provider not mounted (programmer error) → diagnostic msg
 *
 * Extracted from job-detail-sheet.tsx during the NocoDB #4790 Phase 1a
 * PR to keep that file under the gen-003 500-line FAIL threshold.
 */

import type { FleetActivityStreamContextValue } from '@/components/live-activity-cell';
import { parseActivityPayload } from './activity-payload-schemas';
import type { Job } from './types';

/** Mirrors LiveActivityCell + LiveStreamPanel kind tones. */
const KIND_TONE: Record<string, string> = {
  tool_call: 'bg-purple-900/30 text-purple-300',
  artifact: 'bg-green-900/30 text-green-300',
  decision: 'bg-blue-900/30 text-blue-300',
  context_compressed: 'bg-amber-900/30 text-amber-300',
  blocker: 'bg-red-900/30 text-red-300',
  task_started: 'bg-cyan-900/30 text-cyan-300',
  task_completed: 'bg-emerald-900/30 text-emerald-300',
};

/** Job statuses that emit no further events — collapse the section. */
const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'skipped',
  'cancelled',
  'routing_failed',
]);

export function JobLiveActivitySection({
  job,
  fleetCtx,
}: {
  job: Job;
  fleetCtx: FleetActivityStreamContextValue | null;
}) {
  const isTerminal = TERMINAL_STATUSES.has(job.status);

  if (!fleetCtx) {
    return (
      <section>
        <h3 className="mb-3 text-sm font-medium text-neutral-300">
          Live activity
        </h3>
        <p className="text-xs text-neutral-500">
          Fleet stream provider not mounted — open this sheet from
          /agents/:id or /operators.
        </p>
      </section>
    );
  }

  const myEvents = fleetCtx.events
    .filter((e) => {
      const tid = (e.payload as { task_id?: string } | undefined)?.task_id;
      return tid === job.id;
    })
    .slice(-50)
    .reverse(); // newest first

  if (myEvents.length === 0) {
    return (
      <section>
        <h3 className="mb-3 text-sm font-medium text-neutral-300">
          Live activity
        </h3>
        <p className="text-xs text-neutral-500">
          {isTerminal
            ? `Job ${job.status} — no live events to stream. The History tab on the agent detail page has the full event list.`
            : fleetCtx.connected
              ? 'Subscribed — waiting for the runner to emit task events. Events appear here in real time.'
              : 'Connecting to fleet stream…'}
        </p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="mb-3 text-sm font-medium text-neutral-300">
        Live activity{' '}
        <span className="text-xs text-neutral-500">({myEvents.length})</span>
      </h3>
      <div className="space-y-1 rounded-md border border-neutral-800 bg-neutral-900/30 p-2">
        {myEvents.map((event) => {
          const parsed = parseActivityPayload(event.kind, event.payload ?? {});
          let preview = '';
          switch (parsed.kind) {
            case 'tool_call':
              preview = parsed.data.tool;
              break;
            case 'artifact':
              preview = parsed.data.title ?? parsed.data.uri;
              break;
            case 'decision':
              preview = parsed.data.summary;
              break;
            case 'context_compressed':
              preview = `${parsed.data.before_tokens} → ${parsed.data.after_tokens} tok`;
              break;
            case 'blocker':
              preview =
                parsed.data.kind === 'stall_suspected'
                  ? `🤖 ${parsed.data.detail}`
                  : parsed.data.detail;
              break;
            case 'task_started':
              preview = parsed.data.title ?? parsed.data.task_id;
              break;
            case 'task_completed':
              preview = parsed.data.outcome ?? parsed.data.task_id;
              break;
            case 'unknown':
              preview = JSON.stringify(parsed.raw).slice(0, 80);
              break;
          }
          return (
            <div
              key={event.id}
              className="flex items-center gap-2 truncate text-xs"
              title={`${event.kind} — ${event.occurredAt}`}
            >
              <span
                className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${KIND_TONE[event.kind] ?? 'bg-neutral-700 text-neutral-300'}`}
              >
                {event.kind}
              </span>
              <span className="truncate font-mono text-neutral-400">
                {preview}
              </span>
              <span className="ml-auto shrink-0 text-[10px] text-neutral-600">
                {new Date(event.occurredAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
